// src/features/settings/AppearanceCommon.jsx //
import React from "react";
import { useNavigate } from "react-router-dom";

import Radio from "@/components/ui/Radio.jsx";
import Card from "@/components/ui/Card.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { persistThemeForUser } from "@/settings/theme.js";
import supabase from "@/services/supabaseClient.js";

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

export default function AppearanceCommon({
  role = "patient",
  backTo = "",
  onPersistTheme,
}) {
  const nav = useNavigate();
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const { theme, actualTheme, setTheme } = useTheme();
  const auth = useAuth() || {};
  const basePath =
    role === "doctor"
      ? "/settings/doctor"
      : role === "pharmacy"
        ? "/pharmacy/settings"
        : "/settings/patient";
  const [snackbar, setSnackbar] = React.useState("");

  const themeOptions = React.useMemo(
    () => [
      {
        value: "system",
        label: t(
          "settings.preferences.appearance.options.system.label",
          isFrench ? "Utiliser le réglage de l’appareil" : "Use device setting",
        ),
        support: t(
          "settings.preferences.appearance.options.system.support",
          isFrench
            ? "Suit le mode clair ou sombre de votre appareil tout en conservant l’apparence AfriMed™."
            : "Follow your phone's light or dark setting while keeping the AfriMed™ look and feel.",
        ),
      },
      {
        value: "light",
        label: t("settings.preferences.appearance.options.light.label", isFrench ? "Clair" : "Light"),
        support: t(
          "settings.preferences.appearance.options.light.support",
          isFrench
            ? "Le thème clair AfriMed™ utilisé dans l’application."
            : "The familiar AfriMed™ light theme used across the app.",
        ),
      },
      {
        value: "dark",
        label: t("settings.preferences.appearance.options.dark.label", isFrench ? "Sombre" : "Dark"),
        support: t(
          "settings.preferences.appearance.options.dark.support",
          isFrench ? "Le thème sombre AfriMed™ pour les environnements moins lumineux." : "AfriMed™ dark theme for lower-light viewing.",
        ),
      },
    ],
    [isFrench, t],
  );
  const actualThemeLabel =
    actualTheme === "dark"
      ? t("settings.preferences.appearance.options.dark.label", isFrench ? "sombre" : "Dark")
      : actualTheme === "light"
        ? t("settings.preferences.appearance.options.light.label", isFrench ? "clair" : "Light")
        : actualTheme;

  const openSnack = React.useCallback((message) => {
    setSnackbar(message);
    window.clearTimeout(openSnack._t);
    openSnack._t = window.setTimeout(() => setSnackbar(""), 3000);
  }, []);

  async function handleSelect(value) {
    if (value === theme) return;
    setTheme(value);
    persistThemeForUser(auth?.user?.id, value);
    if (auth?.user?.id) {
      try {
        await supabase.auth.updateUser({
          data: {
            ...(auth?.user?.user_metadata || {}),
            theme_preference: value,
          },
        });
      } catch {
        // local preference is already persisted; auth metadata can retry on a future change
      }
    }
    if (typeof onPersistTheme === "function") {
      try {
        await onPersistTheme(value);
      } catch {
        // keep local theme responsive even if persistence fails
      }
    }
    const label = themeOptions.find((option) => option.value === value)?.label;
    if (label) {
      openSnack(
        t("settings.preferences.appearance.selected", isFrench ? "Thème {label} sélectionné" : "{label} theme selected", {
          label,
        }),
      );
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        title={t("settings.preferences.appearance.title", isFrench ? "Apparence" : "Appearance")}
        onBack={() => nav(backTo || `${basePath}/preferences`)}
      />

      <div className="mt-4 space-y-4">
        <Card className="overflow-hidden">
          {themeOptions.map((option, index) => {
            const isActive = theme === option.value;
            const isSystemAndResolved = option.value === "system";
            const modeLabel =
              isSystemAndResolved && actualTheme
                ? t(
                    "settings.preferences.appearance.currentlyUsing",
                    isFrench ? "AfriMed™ utilise actuellement le thème {theme}." : "Currently using AfriMed™ {theme}.",
                    { theme: actualThemeLabel },
                  )
                : null;

            return (
              <React.Fragment key={option.value}>
                <div className="w-full px-4 py-4 text-left">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-title-medium text-on-surface">
                        {option.label}
                      </div>
                      <p className="mt-1 text-body-medium text-on-surface-var">
                        {option.support}
                      </p>
                      {modeLabel ? (
                        <p className="mt-1 text-body-small text-on-surface-var">
                          {modeLabel}
                        </p>
                      ) : null}
                    </div>

                    <div className="pt-0.5">
                      <Radio
                        checked={isActive}
                        onChange={() => handleSelect(option.value)}
                        name={option.label}
                        variant="afrimed"
                      />
                    </div>
                  </div>
                </div>

                {index < themeOptions.length - 1 ? (
                  <div
                    className="ml-4 h-px"
                    style={{ background: "var(--Schemes-Outline-Variant,#E6E7EB)" }}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </Card>

        <p className="px-0.5 text-body-medium text-on-surface-var">
          {t(
            "settings.preferences.appearance.footer",
            isFrench
              ? "Le réglage de l’appareil suit votre préférence d’apparence tout en appliquant les palettes claires et sombres d’AfriMed™."
              : "Use device setting follows your phone's appearance preference while still applying AfriMed™’s own light and dark palettes.",
          )}
        </p>
      </div>

      <Snackbar open={!!snackbar}>{snackbar}</Snackbar>
    </SettingsScreenShell>
  );
}
